import './styles.css';
import { adFormats } from './platform/release-features';
import { Capacitor } from '@capacitor/core';
import { packs, packById, packsForLocale, compatiblePackVersion, type WordPack } from './content/packs';
import { lineCells, type Board, type GenerateOptions } from './core/board';
import { rememberTargets, variedCandidates } from './core/vocabulary';
import { challengeCount, challengeWords, commonnessWords, isChallenge } from './core/challenge';
import { createGame, submitSelection, targetScore, unusedHintPoints, useHint, type GameState, type Lexicon } from './core/game';
import { dictionaryVersion, english, spanish } from './core/language';
import { currentLocale, number, setLocale, t, translatePage } from './i18n';
import { Soundscape } from './platform/audio';
import { generateInWorker, generateWithFallback } from './platform/generation';
import { WebInstall, type InstallPrompt } from './platform/web-install';
import { offlineReadiness, type OfflineState } from './platform/offline-readiness';
import { ScreenAwake } from './platform/screen-awake';
import { grantOnce, MockAds, type AdFormat } from './platform/ads';
import { NativeAds, nativeAdBridge, type BannerState } from './platform/native-ads';
import { ArtBudgetError, artStorageState, cachedArt, cancelArtDownload, clearUnretainedArt, deleteArtPack, downloadArtPack, generateTheme, setArtAutomatic, setArtBudget, setArtRetention, touchArtPack, validateGenerated } from './platform/content';
import { optionalArtPack, optionalArtPacks } from './content/art-packs';
import { starterScene, type DownloadedScene } from './content/scenes';
import { chooseRewardView } from './content/reward-views';
import { rememberTheme, rotatingTheme } from './content/theme-rotation';
import { ArtCleanupUnavailable, setActiveArtPack } from './platform/art-use';
import { createId } from './platform/identity';
import { Storage, defaultSnapshot, type Preferences, type Snapshot } from './platform/storage';
import { Tilt } from './platform/tilt';
import { customDimensions, isPhone, lockPhonePortrait } from './platform/mobile-layout';
import { RewardFeedback } from './platform/reward-feedback';
import { Scenery } from './platform/scenery';
import { PreparedBoards, type BoardRequest } from './platform/prepared-boards';
import { installNativeBack, nativePlatform } from './platform/native-navigation';
import { saveProgressFile } from './platform/progress-export';

const element = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing app element: ${id}`);
  return value as T;
};
const grid = element('letter-grid');
const stage = element('board-stage');
const lines = document.getElementById('lines') as unknown as SVGSVGElement;
const storage = new Storage(), sound = new Soundscape(renderAudioState), tilt = new Tilt();
let tiltRequestPending = false, tiltPermissionAttempted = false;
let snapshot: Snapshot = defaultSnapshot();
const lexicons: Record<string, Lexicon> = Object.fromEntries([english, spanish].map(language => [language.locale,
  { language, permitted: new Set(packsForLocale(language.locale).flatMap(pack => pack.words)), blocked: new Set<string>() }]));
const lexiconFor = (locale: string): Lexicon => lexicons[locale] ?? lexicons.en!;
const activeLexicon = (): Lexicon => lexiconFor(snapshot.game?.board.locale ?? snapshot.preferences.wordLocale);
let availablePacks = [...packs];
const frequencyRanks = new Map<string, Map<string, number>>();
let busy = true;
let saveReady = false, nativeActive = true, pagePresent = true, pageFrozen = false;
const isActive = () => nativeActive && pagePresent && !pageFrozen && !document.hidden;
let lifecycleActive = isActive();
let nativeAdActive = false;
let nativeRequestActive = false, nativeBannerOverlay = false;
let bannerState: BannerState = { enabled: false, status: 'hidden' };
const nativeAdsEnabled = Capacitor.getPlatform() === 'android';
let selectionStart: number | null = null, previewEnd: number | null = null;
let previewPoint: { x: number; y: number } | null = null;
let previewFrame = 0;
let pointer: { id: number; start: number; end: number; moved: boolean; boardId: string } | null = null;
let hintCells: number[] = [];
let hintTimer = 0;
const awake = new ScreenAwake('wakeLock' in navigator ? () => navigator.wakeLock.request('screen') : undefined, renderAwakeState);
let adResolve: ((earned: boolean) => void) | null = null;
let lastInterstitial = 0;
let waitingWorker: ServiceWorker | null = null;
let offlineState: OfflineState = 'checking', offlineEpoch = 0;
const webInstall = new WebInstall(renderInstallState, (() => { try { return window.localStorage; } catch { return undefined; } })());
const artUrls = new Map<string, DownloadedScene>();
function forgetArt(id: string): void {
  const scene = artUrls.get(id);
  if (scene) for (const src of new Set([scene.portrait.src, scene.landscape.src])) URL.revokeObjectURL(src);
  artUrls.delete(id);
}
let lastArtActivation = '';
let scorePresentation: { boardId: string; score: number; hints: number } | null = null;
let pendingResults: { boardId: string; group: string } | null = null;
const scenery = new Scenery(element<HTMLImageElement>('game-scenery'), element<HTMLDialogElement>('artwork-viewer'));
const feedback = new RewardFeedback({
  currentBoard: () => snapshot.game?.board.id,
  reducedMotion: () => snapshot.preferences.reducedMotion || matchMedia('(prefers-reduced-motion: reduce)').matches,
  cell: index => grid.querySelector<HTMLElement>(`[data-cell="${index}"]`),
  hints: element('hint-count'), score: element('score'), card: element('play-board'), chime: kind => sound.reward(kind),
});
function cancelFeedback(): void { feedback.cancel(); sound.cancelRewards(); scorePresentation = null; }
function revealResults(): void {
  const pending = pendingResults, game = snapshot.game;
  if (!pending || !game || pending.boardId !== game.board.id || game.status !== 'completed' || busy || feedback.running || !isActive() || nativeAdActive || adResolve || document.querySelector('dialog[open]')) return;
  pendingResults = null;
  void scenery.present('reveal', t('showScore'), t('swipeReady')).then(async dismissed => {
    if (busy || snapshot.game?.board.id !== pending.boardId) return;
    if (!dismissed) { pendingResults = pending; return; }
    if (isActive() && adFormats.interstitial && snapshot.records.length >= 3 && snapshot.records.length % 3 === 0 && Date.now() - lastInterstitial >= 300000) {
      lastInterstitial = Date.now();
      await ads.show('interstitial').catch(() => ({ earned: false, transactionId: '' }));
    }
    if (busy || snapshot.game?.board.id !== pending.boardId) return;
    if (!isActive() || nativeAdActive || document.querySelector('dialog[open]')) { pendingResults = pending; return; }
    showResults(game, pending.group);
  });
}
document.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('close', revealResults));

const packTitle = (pack: WordPack): string => 'title' in pack ? String(pack.title) : t(pack.nameKey);
const currentPack = (): WordPack => availablePacks.find(pack => pack.id === (snapshot.game?.board.packId ?? snapshot.preferences.pack)) ?? packById('forest');
const messages = new Map<string, { key: string; values: Record<string, string | number> }>();
function renderMessages(only?: string): void {
  for (const [id, message] of messages) {
    if (only && only !== id) continue;
    const values = Object.fromEntries(Object.entries(message.values).map(([key, value]) => [key, typeof value === 'number' ? number(value) : value]));
    const node = element(id), text = t(message.key, values);
    if (node.textContent !== text) node.textContent = text;
  }
}
function message(id: string, key: string, values: Record<string, string | number> = {}): void {
  messages.set(id, { key, values: { ...values } }); renderMessages(id);
}
function status(key: string, values: Record<string, string | number> = {}): void { message('status', key, values); }
async function save(): Promise<boolean> {
  if (!saveReady) return false;
  try { await storage.save(snapshot); renderProgress(); element('save-status').textContent = t('saved'); return true; }
  catch {
    element('save-status').textContent = t('saveFailed');
    if (storage.notice) {
      element('recovery-notice').hidden = false;
      element('recovery-notice').textContent = t(`storageNotice.${storage.notice}`);
    }
    return false;
  }
}
function renderProgress(): void {
  const totals = storage.progress;
  element('progress-points').textContent = number(totals.points);
  element('progress-boards').textContent = number(totals.boards);
  element('progress-words').textContent = number(totals.words);
  element('progress-history-note').hidden = !totals.limited;
}
function applyPreferences(): void {
  const prefs = snapshot.preferences;
  if (!['en', 'es'].includes(prefs.wordLocale)) prefs.wordLocale = 'en';
  const choices = availablePacks.filter(pack => pack.language.locale === prefs.wordLocale);
  const selected = availablePacks.find(pack => pack.id === prefs.pack);
  if (prefs.pack !== 'rotating' && !choices.some(pack => pack.id === prefs.pack)) prefs.pack = (choices.find(pack => pack.nameKey === selected?.nameKey) ?? choices[0]!).id;
  const phone = isPhone();
  document.documentElement.toggleAttribute('data-phone', phone);
  Object.assign(prefs, customDimensions(prefs.rows, prefs.columns, phone));
  const dimensions = element<HTMLFormElement>('preferences-form');
  for (const [key, min, max] of [['rows', phone ? 8 : 3, phone ? 16 : 32], ['columns', phone ? 5 : 3, phone ? 8 : 24]] as const) {
    const input = dimensions.elements.namedItem(key) as HTMLInputElement;
    input.min = String(min); input.max = String(max);
  }
  if (nativeAdsEnabled) {
    for (const [source, target] of Object.entries({ adLabel: 'nativeAdLabel', adBody: 'nativeAdBody', adHint: 'nativeAdHint', privacyText: 'nativePrivacyText' })) {
      document.querySelectorAll<HTMLElement>(`[data-i18n="${source}"]`).forEach(node => { node.dataset.i18n = target; });
    }
  }
  element('ad-privacy-choices').hidden = !nativeAdsEnabled;
  setLocale(prefs.locale); translatePage(); renderMessages(); renderAwakeState(); renderFocusState(); renderAudioState(); renderOfflineState();
  renderProgress();
  element('challenge-help').textContent = t(`challengeHelp.${prefs.challenge}`);
  renderArtPackDetails();
  void renderArtStorage();
  const recoveryNotice = element('recovery-notice');
  recoveryNotice.hidden = !storage.notice;
  if (storage.notice) recoveryNotice.textContent = t(`storageNotice.${storage.notice}`);
  if (storage.notice === 'newer' || storage.notice === 'unavailable' || storage.notice === 'conflict') element('save-status').textContent = t('saveFailed');
  document.documentElement.toggleAttribute('data-contrast', prefs.contrast);
  document.documentElement.toggleAttribute('data-reduced-motion', prefs.reducedMotion);
  document.documentElement.style.setProperty('--min-cell', prefs.roomier ? '44px' : '32px');
  sound.enabled = prefs.sound;
  if (prefs.sound || prefs.music) sound.prepare();
  sound.volumes(prefs.effectsVolume, prefs.musicVolume); tilt.reducedMotion = prefs.reducedMotion;
  if (isActive() && !nativeAdActive && !adResolve) sound.resume(prefs.music); else sound.suspend();
  void syncTilt();
  element('connection').textContent = t(navigator.onLine ? 'online' : 'offline');
  const select = element<HTMLSelectElement>('pack-select');
  select.replaceChildren(...choices.map(pack => {
    const option = document.createElement('option'); option.value = pack.id; option.textContent = packTitle(pack); return option;
  }));
  const rotatingOption = document.createElement('option'); rotatingOption.value = 'rotating'; rotatingOption.textContent = t('pack.rotating'); select.append(rotatingOption);
  const form = element<HTMLFormElement>('preferences-form');
  for (const [key, value] of Object.entries(prefs)) {
    const input = form.elements.namedItem(key) as HTMLInputElement | HTMLSelectElement | null;
    if (!input) continue;
    if (input instanceof HTMLInputElement && input.type === 'checkbox') input.checked = Boolean(value);
    else input.value = String(value);
  }
  if (snapshot.game) render(false);
  else { setActiveArtPack(); lastArtActivation = ''; }
  renderInstallState(); renderBannerControls(); syncBannerVisibility();
  warmNextBoards();
}

function render(rebuild: boolean): void {
  element<HTMLAnchorElement>('support-email').href = `mailto:scry.support@sau.cx?subject=${encodeURIComponent(t('supportSubject'))}&body=${encodeURIComponent(t('supportBody', { build: import.meta.env.VITE_BUILD_ID, language: snapshot.game?.board.locale ?? snapshot.preferences.wordLocale }))}`;
  const game = snapshot.game;
  element('mock-banner').hidden = !adFormats.rewarded;
  element('banner-button').closest<HTMLElement>('section')!.hidden = !adFormats.banner;
  element('reward-button').hidden = !adFormats.rewarded;
  element('banner-button').hidden = !adFormats.banner;
  element('banner-status').hidden = !adFormats.banner;
  if (!game) { setActiveArtPack(); lastArtActivation = ''; return; }
  const board = game.board;
  const world = currentPack();
  const theme = world.nameKey.split('.').at(-1) ?? 'forest';
  const activeArt = artUrls.has(theme) ? theme : undefined;
  setActiveArtPack(activeArt);
  const activation = activeArt ? `${board.id}:${activeArt}` : '';
  if (activation !== lastArtActivation) { lastArtActivation = activation; if (activeArt) void touchArtPack(activeArt).catch(() => {}); }
  const scene = starterScene(theme);
  const reward = chooseRewardView(snapshot.rewardViews ?? { recent: [] }, board.id, scene?.id ?? '');
  snapshot.rewardViews = reward.state;
  scenery.setWorld(theme, packTitle(world), game.status === 'completed', artUrls.get(theme), reward.view);
  for (const id of ['letter-grid', 'target-list', 'bonus-list', 'dialog-targets']) element(id).lang = board.locale;
  stage.style.setProperty('--columns', String(board.columns));
  lines.setAttribute('viewBox', `0 0 ${board.columns * 100} ${board.rows * 100}`);
  lines.setAttribute('preserveAspectRatio', 'none');
  if (rebuild) {
    grid.setAttribute('aria-rowcount', String(board.rows)); grid.setAttribute('aria-colcount', String(board.columns));
    const cells = board.letters.map((letter, index) => {
      const cell = document.createElement('button'); cell.className = 'tile'; cell.type = 'button'; cell.dataset.cell = String(index);
      cell.setAttribute('role', 'gridcell'); cell.tabIndex = index === 0 ? 0 : -1;
      cell.setAttribute('aria-colindex', String(index % board.columns + 1));
      const glyph = document.createElement('span'); glyph.textContent = letter; cell.append(glyph); return cell;
    });
    grid.replaceChildren(...Array.from({ length: board.rows }, (_, index) => {
      const row = document.createElement('div'); row.className = 'board-row'; row.setAttribute('role', 'row');
      row.setAttribute('aria-rowindex', String(index + 1)); row.append(...cells.slice(index * board.columns, (index + 1) * board.columns)); return row;
    }));
  }
  grid.querySelectorAll<HTMLButtonElement>('.tile').forEach((cell, index) => {
    cell.setAttribute('aria-label', t('tile', { row: Math.floor(index / board.columns) + 1, column: index % board.columns + 1, letter: board.letters[index]! }));
    cell.toggleAttribute('data-selected', selectionStart === index);
    cell.setAttribute('aria-selected', String(selectionStart === index));
    cell.toggleAttribute('data-hint', hintCells.includes(index));
  });
  const presentation = scorePresentation?.boardId === board.id ? scorePresentation : null;
  const shownScore = presentation?.score ?? game.score, goal = targetScore(game);
  element('score').textContent = `${number(shownScore)} / ${number(goal)}`;
  element('score').setAttribute('aria-label', t('scoreTarget', { score: number(shownScore), target: number(goal) }));
  element('play-board').classList.toggle('target-beaten', shownScore > goal);
  element('target-achievement').hidden = shownScore <= goal;
  element('found-count').textContent = `${number(game.found.length)} / ${number(board.targets.length)}`;
  element('hint-count').textContent = number(presentation?.hints ?? game.hints + snapshot.extraHints);
  element('progress-label').textContent = `${number(game.found.length)} / ${number(board.targets.length)}`;
  const progress = Math.round(game.found.length / board.targets.length * 100);
  const progressElement = element('progress');
  progressElement.setAttribute('aria-valuenow', String(progress));
  progressElement.setAttribute('aria-valuetext', t('progress', { found: game.found.length, total: board.targets.length }));
  progressElement.querySelector<HTMLElement>('span')!.style.width = `${progress}%`;
  element('board-title').textContent = packTitle(currentPack());
  element('board-eyebrow').textContent = `${board.columns} × ${board.rows} · ${board.locale.toLocaleUpperCase(currentLocale())} · ${t(`challenge.${board.challenge ?? 'classic'}`)}`;
  element('target-list').replaceChildren(...board.targets.map(target => {
    const li = document.createElement('li'); li.textContent = target.word; li.dataset.word = target.word;
    if (game.found.includes(target.word)) { li.className = 'found'; li.setAttribute('aria-label', `${target.word} ✓`); }
    return li;
  }));
  const bonuses = game.credited.filter(word => !board.targets.some(target => target.word === word));
  element('empty-bonus').hidden = bonuses.length > 0;
  element('bonus-list').replaceChildren(...bonuses.map(word => { const li = document.createElement('li'); li.textContent = word; return li; }));
  const hintButton = element<HTMLButtonElement>('hint-button');
  const hintAction = game.status === 'completed' ? 'newGame' : 'hint';
  hintButton.dataset.i18n = hintAction;
  hintButton.textContent = t(hintAction);
  hintButton.disabled = busy || nativeAdActive;
  element<HTMLButtonElement>('reward-button').disabled = nativeAdActive || game.status === 'completed';
  renderLines(); renderInstallState();
  void manageWakeLock();
  requestAnimationFrame(fitBoard);
}
function fitBoard(): void {
  if (!snapshot.game || pointer) return;
  const board = snapshot.game.board;
  const container = stage.parentElement!;
  const availableWidth = container.clientWidth - 8;
  const minimum = snapshot.preferences.roomier ? 44 : 32;
  const viewportBottom = (visualViewport?.offsetTop ?? 0) + (visualViewport?.height ?? innerHeight);
  const appBottom = document.documentElement.hasAttribute('data-mock-banner') ? element('app').getBoundingClientRect().bottom : viewportBottom;
  const footer = element('play-board').querySelector<HTMLElement>('.board-footer')!;
  const reserve = footer.offsetHeight + element('status').offsetHeight + 48;
  const remainingHeight = Math.max(0, Math.min(viewportBottom, appBottom) - Math.max(0, stage.getBoundingClientRect().top) - reserve);
  const gaps = (board.columns - 1) * 3;
  const cellSize = Math.max(minimum, Math.min(44, (availableWidth - gaps) / board.columns, (remainingHeight - (board.rows - 1) * 3) / board.rows));
  stage.style.minWidth = `${board.columns * minimum + gaps}px`;
  stage.style.width = `${board.columns * cellSize + gaps}px`;
  stage.style.setProperty('--tile-size', `${cellSize}px`);
}

function makeLine(start: number, end: number, className: string, color?: string): SVGLineElement {
  const columns = snapshot.game!.board.columns;
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  for (const [key, value] of Object.entries({ x1: (start % columns) * 100 + 50, y1: Math.floor(start / columns) * 100 + 50, x2: (end % columns) * 100 + 50, y2: Math.floor(end / columns) * 100 + 50 })) line.setAttribute(key, String(value));
  line.setAttribute('class', className); if (color) line.style.stroke = color;
  return line;
}
function renderLines(): void {
  if (!snapshot.game) return;
  const colors = ['#5eab84', '#e2b366', '#9f8dc5', '#79acbd', '#bf8194'];
  const accepted = snapshot.game.accepted.map((path, i) => makeLine(path.cells[0]!, path.cells.at(-1)!, 'accepted', colors[i % colors.length]));
  if (selectionStart !== null && previewEnd !== null) {
    const { rows, columns } = snapshot.game.board;
    const cells = lineCells(rows, columns, selectionStart, previewEnd);
    const preview = makeLine(selectionStart, previewEnd, cells ? 'preview' : 'preview invalid');
    if (previewPoint) { preview.setAttribute('x2', String(previewPoint.x)); preview.setAttribute('y2', String(previewPoint.y)); }
    const halo = preview.cloneNode() as SVGLineElement;
    halo.setAttribute('class', 'preview-halo');
    accepted.push(halo, preview);
    const panel = element('selection-preview');
    panel.hidden = false; panel.setAttribute('aria-label', t('liveSelection'));
    const forward = cells ? cells.map(cell => snapshot.game!.board.letters[cell]).join('') : '';
    const reverse = cells ? [...cells].reverse().map(cell => snapshot.game!.board.letters[cell]).join('') : '';
    for (const [id, word] of [['selection-forward', forward], ['selection-reverse', reverse]]) {
      const label = element(id!);
      label.textContent = activeLexicon().blocked.has(word!) ? '•••' : word!;
      label.hidden = !word || (id === 'selection-reverse' && forward === reverse);
      label.toggleAttribute('data-valid', activeLexicon().permitted.has(word!) && !activeLexicon().blocked.has(word!));
    }
    element('selection-length').textContent = cells ? t('selectionLength', { count: cells.length }) : t('invalid');
  } else {
    element('selection-preview').hidden = true;
  }
  lines.replaceChildren(...accepted);
}
function queuePreview(): void {
  if (previewFrame) return;
  previewFrame = requestAnimationFrame(() => { previewFrame = 0; renderLines(); });
}
function clearSelection(): void {
  cancelAnimationFrame(previewFrame); previewFrame = 0;
  pointer = null; selectionStart = null; previewEnd = null; previewPoint = null;
  const selected = grid.querySelector('[data-selected]');
  selected?.removeAttribute('data-selected'); selected?.setAttribute('aria-selected', 'false'); renderLines(); scheduleLayout();
}
function cellAt(x: number, y: number): number | null {
  const hit = document.elementFromPoint(x, y);
  const cell = hit?.closest<HTMLElement>('[data-cell]');
  if (cell && grid.contains(cell)) return Number(cell.dataset.cell);
  if (!snapshot.game || (hit !== grid && hit !== stage && !hit?.classList.contains('board-row'))) return null;
  const rect = grid.getBoundingClientRect(), board = snapshot.game.board;
  if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) return null;
  return Math.floor((y - rect.top) / rect.height * board.rows) * board.columns + Math.floor((x - rect.left) / rect.width * board.columns);
}
function selectCell(index: number): void {
  const previous = grid.querySelector('[data-selected]'); previous?.removeAttribute('data-selected'); previous?.setAttribute('aria-selected', 'false');
  const selected = grid.querySelector<HTMLElement>(`[data-cell="${index}"]`);
  selected?.setAttribute('data-selected', ''); selected?.setAttribute('aria-selected', 'true');
  if (snapshot.game) status('selectionStarted', { row: Math.floor(index / snapshot.game.board.columns) + 1, column: index % snapshot.game.board.columns + 1 });
}
function submit(start: number, end: number, boardId: string): void {
  if (busy || nativeAdActive || !isActive() || !snapshot.game) return;
  const before = snapshot.game;
  const result = submitSelection(before, start, end, activeLexicon(), boardId, snapshot.extraHints);
  clearSelection();
  if (result.outcome !== 'accepted') {
    if (result.outcome !== 'completed' && result.outcome !== 'stale') status(result.outcome);
    if (result.outcome !== 'completed' && result.outcome !== 'stale') sound.reward('invalid'); return;
  }
  snapshot.game = result.state;
  if (result.state.status === 'completed') snapshot.extraHints = 0;
  const hintCount = result.entry!.hintCount ?? 0;
  if (hintCount) scorePresentation = { boardId, score: result.state.score - (result.entry!.unusedHints ?? 0), hints: result.state.hints + snapshot.extraHints + hintCount };
  render(false); status('accepted', { words: result.words.join(' / '), points: result.entry!.total });
  if (result.state.status === 'completed') {
    const board = result.state.board;
    const group = `${result.state.rulesVersion}:${board.packId}:${board.columns}x${board.rows}:${result.state.assisted ? 'assisted' : 'unassisted'}${result.entry!.adHintCount ? ':ad-bonus' : ''}${board.challengeVersion ? `:${board.challengeVersion}:${board.challenge}` : ''}`;
    if (!snapshot.records.some(record => record.boardId === board.id)) snapshot.records.push({ boardId: board.id, group, score: result.state.score, when: Date.now(), targets: board.targets.map(target => target.word), wordCount: result.state.credited.length });
    snapshot.records = snapshot.records.slice(-100);
    pendingResults = { boardId, group };
  }
  void save();
  void feedback.enqueue({ boardId, readings: result.readings, hintCount, completed: result.state.status === 'completed',
    exceeded: before.score <= targetScore(before) && result.state.score > targetScore(result.state),
    onHint: count => {
      if (scorePresentation?.boardId !== boardId) return;
      scorePresentation.score = result.state.score - (hintCount - count) * unusedHintPoints;
      scorePresentation.hints = result.state.hints + snapshot.extraHints + hintCount - count;
      render(false);
    },
  }).catch(() => { /* Optional effects never prevent committed progress/results. */ }).finally(() => {
    if (result.state.status === 'completed' && snapshot.game?.board.id === boardId) {
      scorePresentation = null; render(false); revealResults();
    }
    renderInstallState();
  });
  renderInstallState();
}
function showResults(game: GameState, group: string): void {
  element('result-score').textContent = `${number(game.score)} / ${number(targetScore(game))}`;
  const best = Math.max(0, ...snapshot.records.filter(record => record.group === group).map(record => record.score));
  element('result-detail').textContent = `${t(game.assisted ? 'assisted' : 'unassisted')} · ${t('best')}: ${number(best)}${game.score > targetScore(game) ? ` · ${t('targetBeaten')}` : ''}`;
  element('result-ledger').replaceChildren(...game.ledger.map(entry => {
    const row = document.createElement('div'), word = document.createElement('span'), points = document.createElement('span');
    word.textContent = entry.words.join(' / '); points.textContent = `+${number(entry.total)}`; row.append(word, points);
    row.title = t('ledgerBreakdown', { base: entry.base, target: entry.target, special: entry.special, completion: entry.completion }) + t('hintBonusBreakdown', { points: entry.unusedHints ?? 0 });
    return row;
  }));
  element<HTMLDialogElement>('results').showModal();
}

// Keep noneditable app text immersive; preserve text-field editing and scrolling.
for (const type of ['contextmenu', 'selectstart', 'dragstart', 'dblclick']) {
  document.addEventListener(type, event => {
    const target = event.target instanceof Element ? event.target : event.target instanceof Node ? event.target.parentElement : null;
    if (target && !target.closest('input, textarea, [contenteditable="true"]')) event.preventDefault();
  });
}

grid.addEventListener('pointerdown', event => {
  if (busy || nativeAdActive || !isActive() || !snapshot.game || snapshot.game.status !== 'playing' || !event.isPrimary || event.button !== 0 || pointer) return;
  const cell = cellAt(event.clientX, event.clientY); if (cell === null) return;
  unlockAudio();
  if (selectionStart !== null) { const start = selectionStart; submit(start, cell, snapshot.game.board.id); return; }
  selectionStart = cell; previewEnd = cell; selectCell(cell);
  pointer = { id: event.pointerId, start: cell, end: cell, moved: false, boardId: snapshot.game.board.id };
  grid.setPointerCapture(event.pointerId); sound.reward('select'); renderLines();
});
grid.addEventListener('pointermove', event => {
  if (!pointer || pointer.id !== event.pointerId) return;
  const rect = grid.getBoundingClientRect(), board = snapshot.game!.board;
  previewPoint = { x: Math.max(0, Math.min(board.columns * 100, (event.clientX - rect.left) / rect.width * board.columns * 100)), y: Math.max(0, Math.min(board.rows * 100, (event.clientY - rect.top) / rect.height * board.rows * 100)) };
  const cell = cellAt(event.clientX, event.clientY); if (cell === null) { queuePreview(); return; }
  if (cell !== pointer.end) sound.reward('select');
  pointer.end = cell; pointer.moved ||= cell !== pointer.start; previewEnd = cell; queuePreview();
});
grid.addEventListener('pointerup', event => {
  if (!pointer || pointer.id !== event.pointerId) return;
  const gesture = pointer, end = cellAt(event.clientX, event.clientY); pointer = null; scheduleLayout();
  if (grid.hasPointerCapture(event.pointerId)) grid.releasePointerCapture(event.pointerId);
  if (end === null) { clearSelection(); return; }
  if (gesture.moved || end !== gesture.start) submit(gesture.start, end, gesture.boardId);
});
grid.addEventListener('pointercancel', clearSelection);
grid.addEventListener('lostpointercapture', () => { if (pointer) clearSelection(); });
grid.addEventListener('focus', () => { grid.querySelector<HTMLElement>('.tile[tabindex="0"]')?.focus(); });
grid.addEventListener('focusin', event => {
  const cell = (event.target as HTMLElement).closest<HTMLElement>('.tile');
  if (!cell) return;
  grid.querySelectorAll<HTMLElement>('.tile').forEach(tile => { tile.tabIndex = tile === cell ? 0 : -1; });
});
grid.addEventListener('keydown', event => {
  if (busy || nativeAdActive || !isActive() || !snapshot.game) return;
  const board = snapshot.game.board;
  const active = (event.target as HTMLElement).closest<HTMLElement>('[data-cell]');
  let index = active ? Number(active.dataset.cell) : 0;
  if (event.key === 'Escape') { event.preventDefault(); clearSelection(); status('selectionCancelled'); return; }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault(); unlockAudio();
    if (selectionStart === null) { selectionStart = index; previewEnd = index; selectCell(index); renderLines(); }
    else submit(selectionStart, index, board.id);
    return;
  }
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const row = Math.floor(index / board.columns), column = index % board.columns;
  if (event.key === 'ArrowUp') index = Math.max(0, row - 1) * board.columns + column;
  if (event.key === 'ArrowDown') index = Math.min(board.rows - 1, row + 1) * board.columns + column;
  if (event.key === 'ArrowLeft') index = row * board.columns + Math.max(0, column - 1);
  if (event.key === 'ArrowRight') index = row * board.columns + Math.min(board.columns - 1, column + 1);
  if (event.key === 'Home') index = event.ctrlKey ? 0 : row * board.columns;
  if (event.key === 'End') index = event.ctrlKey ? board.letters.length - 1 : row * board.columns + board.columns - 1;
  grid.querySelectorAll<HTMLButtonElement>('.tile').forEach(cell => { cell.tabIndex = -1; });
  const target = grid.querySelector<HTMLButtonElement>(`[data-cell="${index}"]`)!; target.tabIndex = 0; target.focus();
  if (selectionStart !== null) { previewEnd = index; renderLines(); }
});
let layoutFrame = 0;
function scheduleLayout(): void {
  if (layoutFrame) return;
  layoutFrame = requestAnimationFrame(() => { layoutFrame = 0; fitBoard(); });
}
// Observe external constraints, never rebuild tiles or cancel an active reward on resize.
const boardContainer = stage.parentElement!;
let containerWidth = boardContainer.clientWidth;
const layoutObserver = new ResizeObserver(entries => {
  for (const entry of entries) {
    if (entry.target === boardContainer) {
      const width = boardContainer.clientWidth;
      if (width === containerWidth) continue;
      containerWidth = width;
    }
    scheduleLayout();
  }
});
layoutObserver.observe(boardContainer);
layoutObserver.observe(element('app'));
window.addEventListener('resize', scheduleLayout);
visualViewport?.addEventListener('resize', scheduleLayout);

document.addEventListener('pointerdown', () => { void lockPhonePortrait(); }, { passive: true });
let hadFullscreen = Boolean(document.fullscreenElement);
const installedDisplay = matchMedia('(display-mode: standalone)');
const fullscreenDisplay = matchMedia('(display-mode: fullscreen)');
function renderFocusState(): void {
  const focused = document.body.classList.contains('focus-view');
  const mode = Capacitor.isNativePlatform() ? 'native' : document.fullscreenElement ? 'fullscreen'
    : installedDisplay.matches || fullscreenDisplay.matches ? 'installed' : focused ? 'focus' : 'browser';
  const button = element<HTMLButtonElement>('focus-button');
  button.dataset.i18n = focused ? 'focusExit' : 'focus';
  button.textContent = t(button.dataset.i18n);
  button.setAttribute('aria-pressed', String(focused));
  element('display-status').dataset.mode = mode;
  element('display-status').textContent = t(`display.${mode}`);
  renderInstallState(); scheduleLayout();
}
async function leaveFocus(): Promise<void> {
  document.body.classList.remove('focus-view'); renderFocusState();
  if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
}
async function enterFocus(): Promise<void> {
  document.body.classList.add('focus-view'); renderFocusState();
  if (!Capacitor.isNativePlatform() && document.fullscreenEnabled && !document.fullscreenElement) {
    try { await document.documentElement.requestFullscreen({ navigationUI: 'hide' }); }
    catch { /* CSS focus remains available; status reports the actual mode. */ }
  }
  if (!document.body.classList.contains('focus-view')) {
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    return;
  }
  await lockPhonePortrait(); renderFocusState();
}
document.addEventListener('fullscreenchange', () => {
  if (hadFullscreen && !document.fullscreenElement) document.body.classList.remove('focus-view');
  hadFullscreen = Boolean(document.fullscreenElement);
  renderFocusState(); void lockPhonePortrait();
});
installedDisplay.addEventListener('change', renderFocusState);
fullscreenDisplay.addEventListener('change', renderFocusState);

function generation(options: GenerateOptions, signal?: AbortSignal, deadline?: number): Promise<Board> {
  return generateInWorker(options, () => new Worker(new URL('./core/generate.worker.ts', import.meta.url), { type: 'module' }), signal, deadline);
}
let foregroundGeneration: AbortController | null = null;
const preparedBoards = new PreparedBoards(generation);
function nextBoardRequest(prefs = snapshot.preferences): { primary: BoardRequest; fallback: BoardRequest } {
  const compactColumns = Math.max(3, Math.min(9, Math.floor((stage.parentElement!.clientWidth - 5) / (prefs.roomier ? 47 : 35))));
  const custom = customDimensions(prefs.rows, prefs.columns, isPhone());
  const [rows, columns] = prefs.size === 'custom' ? [custom.rows, custom.columns] : prefs.size === 'expanded' ? [20, 16] : prefs.size === 'roomy' ? [16, 12] : [11, compactColumns];
  const defaultPack = packsForLocale(prefs.wordLocale).find(item => item.id !== 'general' && item.id !== 'general-es')!;
  const selected = prefs.pack === 'rotating' ? rotatingTheme(prefs.wordLocale, snapshot.recentThemes ?? {}, snapshot.game?.board.id ?? 'first-board')
    : availablePacks.find(item => item.id === prefs.pack && item.language.locale === prefs.wordLocale) ?? defaultPack;
  const fallbackPack = prefs.pack === 'rotating' ? selected : defaultPack;
  const count = challengeCount(prefs.challenge, rows, columns);
  const recent = snapshot.exposure?.[prefs.wordLocale as 'en' | 'es'] ?? [];
  const mixed = selected.id === 'general' || selected.id === 'general-es', ranks = mixed ? frequencyRanks.get(prefs.wordLocale) : undefined;
  const source = ranks?.size ? [...lexiconFor(prefs.wordLocale).permitted].filter(word => ranks.has(word) && !lexiconFor(prefs.wordLocale).blocked.has(word))
    : prefs.challenge === 'relaxed' && selected.id === 'general' ? packs.find(pack => pack.id === 'general')!.words : selected.words;
  const candidates = commonnessWords(challengeWords(source, prefs.challenge, Math.max(rows, columns), count), ranks, prefs.challenge, count);
  const pack = { ...selected, words: variedCandidates(candidates, recent, Math.max(rows, columns), count, snapshot.game?.board.id ?? 'first-board') };
  const lexicon = lexiconFor(pack.language.locale);
  const fallback = prefs.size === 'custom' ? custom : isPhone() ? customDimensions(11, compactColumns, true) : { rows: 11, columns: 9 };
  return {
    primary: { rows, columns, count, pack, blocked: [...lexicon.blocked], challenge: prefs.challenge },
    fallback: { ...fallback, count: challengeCount(prefs.challenge, fallback.rows, fallback.columns), pack: { ...fallbackPack,
      words: challengeWords(fallbackPack.words, prefs.challenge, Math.max(fallback.rows, fallback.columns), challengeCount(prefs.challenge, fallback.rows, fallback.columns)) }, blocked: [...lexicon.blocked], challenge: prefs.challenge },
  };
}
function warmNextBoards(): void {
  if (busy || nativeAdActive || adResolve || !isActive()) return;
  void preparedBoards.prepare(nextBoardRequest().primary);
}
type NextGameChoice = Pick<Preferences, 'pack' | 'challenge' | 'wordLocale'>;
async function newBoard(ask = true, choice?: NextGameChoice): Promise<void> {
  if (busy || nativeAdActive || adResolve || !isActive()) return;
  if (ask && snapshot.game?.status === 'playing' && snapshot.game.credited.length > 0 && !confirm(t('confirmNew'))) return;
  if (ask && snapshot.game && snapshot.preferences.askNextGame && !choice) { showNextGameChoice(); return; }
  pendingResults = null; scenery.cancel(); cancelFeedback();
  busy = true; clearSelection(); hintCells = []; clearTimeout(hintTimer);
  element<HTMLButtonElement>('new-button').disabled = true; status('generating');
  const request = nextBoardRequest(choice ? { ...snapshot.preferences, ...choice } : snapshot.preferences), controller = new AbortController();
  foregroundGeneration?.abort(); foregroundGeneration = controller;
  try {
    let board = preparedBoards.take(request.primary);
    if (!board) {
      preparedBoards.clear();
      board = await generateWithFallback({ ...request.primary, seed: createId() }, { ...request.fallback, seed: createId() }, generation, controller.signal);
    }
    if (controller.signal.aborted || foregroundGeneration !== controller || !isActive()) throw new DOMException('Generation cancelled', 'AbortError');
    snapshot.game = createGame(board);
    snapshot.exposure = rememberTargets(snapshot.exposure ?? {}, board.locale, board.targets.map(target => target.word));
    snapshot.recentThemes = rememberTheme(snapshot.recentThemes ?? {}, board.locale, board.packId);
    snapshot.extraHints = 0; busy = false; render(true); status('ready'); await save();
    if (isActive()) await scenery.present('preview', t('play'));
  } catch {
    if (foregroundGeneration === controller) {
      busy = false;
      if (!controller.signal.aborted) status('generationError');
      if (snapshot.game) render(false);
    }
  } finally {
    if (foregroundGeneration === controller) {
      foregroundGeneration = null; busy = false;
      element<HTMLButtonElement>('new-button').disabled = false;
      if (controller.signal.aborted && isActive() && !snapshot.game && !nativeAdActive && !adResolve) queueMicrotask(() => { void newBoard(false); });
      warmNextBoards();
    }
  }
}

function presentAd(format: AdFormat): Promise<boolean> {
  foregroundGeneration?.abort(); preparedBoards.clear();
  cancelFeedback();
  clearSelection(); sound.suspend(); tilt.suspend();
  const dialog = element<HTMLDialogElement>('ad-dialog');
  element('ad-title').textContent = t(format === 'rewarded' ? 'adRewardTitle' : 'breakTitle');
  element('ad-body').textContent = t(format === 'rewarded' ? 'adRewardBody' : 'breakBody');
  element('earn-button').hidden = format !== 'rewarded';
  dialog.showModal();
  return new Promise(resolve => { adResolve = resolve; void manageWakeLock(); });
}
function setNativeAdOverlay(source: 'request' | 'banner', active: boolean): void {
  if (source === 'request') nativeRequestActive = active; else nativeBannerOverlay = active;
  nativeAdActive = nativeRequestActive || nativeBannerOverlay;
  if (nativeAdActive) {
    foregroundGeneration?.abort();
    preparedBoards.clear(); clearSelection(); sound.suspend(); tilt.suspend(); cancelFeedback();
    for (const id of ['reward-button', 'hint-button', 'new-button']) element<HTMLButtonElement>(id).disabled = true;
  } else {
    element<HTMLButtonElement>('reward-button').disabled = false;
    element<HTMLButtonElement>('new-button').disabled = busy;
    if (isActive()) { tilt.resume(); sound.resume(snapshot.preferences.music); }
    if (snapshot.game) render(false);
    if (saveReady && !snapshot.game && !busy && isActive() && !adResolve) void newBoard(false);
    revealResults(); warmNextBoards(); requestAnimationFrame(fitBoard);
  }
  void manageWakeLock();
  renderBannerControls(); syncBannerVisibility();
}
const ads = nativeAdsEnabled ? new NativeAds(nativeAdBridge, () => setNativeAdOverlay('request', true), () => setNativeAdOverlay('request', false))
  : new MockAds(presentAd, () => element<HTMLDialogElement>('privacy').showModal());
window.addEventListener('wordhuli:banner-overlay-open', () => setNativeAdOverlay('banner', true));
window.addEventListener('wordhuli:banner-overlay-close', () => setNativeAdOverlay('banner', false));

function renderBannerControls(): void {
  const button = element<HTMLButtonElement>('banner-button');
  button.textContent = t(bannerState.enabled ? 'bannerHide' : nativeAdsEnabled ? 'bannerShowNative' : 'bannerShowMock');
  button.disabled = !saveReady || nativeAdActive || !isActive();
  element('banner-status').textContent = t(`banner.${bannerState.status}`);
}
function updateBannerState(state: BannerState): void {
  if (typeof state.enabled !== 'boolean' || !['hidden', 'loading', 'visible', 'unavailable'].includes(state.status)) return;
  bannerState = state; renderBannerControls();
}
function syncBannerVisibility(): void {
  if (!nativeAdsEnabled || !saveReady) return;
  void nativeAdBridge.bannerVisibility({ visible: isActive() && !nativeAdActive && !document.querySelector('dialog[open]'), label: t('nativeBannerLabel') })
    .then(updateBannerState).catch(() => {});
}
const dialogObserver = new MutationObserver(syncBannerVisibility);
document.querySelectorAll('dialog').forEach(dialog => dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
element('banner-button').addEventListener('click', async () => {
  if (!adFormats.banner || !saveReady || nativeAdActive || !isActive()) return;
  if (ads instanceof NativeAds) updateBannerState(await ads.banner(!bannerState.enabled, false, t('nativeBannerLabel')));
  else {
    bannerState = { enabled: !bannerState.enabled, status: bannerState.enabled ? 'hidden' : 'visible' };
    element('banner-preview').hidden = !bannerState.enabled;
    document.documentElement.toggleAttribute('data-mock-banner', bannerState.enabled);
    renderBannerControls(); requestAnimationFrame(fitBoard);
  }
});
function closeAd(earned: boolean): void {
  element<HTMLDialogElement>('ad-dialog').close();
  const resolve = adResolve; adResolve = null; resolve?.(earned);
  void manageWakeLock();
  if (isActive()) { tilt.resume(); sound.resume(snapshot.preferences.music); }
  if (saveReady && !snapshot.game && !busy && isActive() && !nativeAdActive) void newBoard(false);
  revealResults();
  warmNextBoards();
}
element('earn-button').addEventListener('click', () => closeAd(true));
element('dismiss-ad').addEventListener('click', () => closeAd(false));
element('ad-dialog').addEventListener('cancel', event => { event.preventDefault(); closeAd(false); });
element('reward-button').addEventListener('click', async () => {
  if (!adFormats.rewarded || busy || nativeAdActive || !isActive() || snapshot.game?.status !== 'playing') return;
  const boardId = snapshot.game.board.id;
  const result = await ads.show('rewarded');
  if (ads instanceof NativeAds) {
    try {
      const saved = await ads.recover(snapshot, save); render(false);
      status(result.earned ? (saved ? 'adEarned' : 'saveFailed') : result.transactionId ? 'nativeAdDismissed' : 'adUnavailable');
    } catch { status('saveFailed'); }
    return;
  }
  if (result.earned && grantOnce(snapshot.grants, result.transactionId)) {
    const eligible = snapshot.game?.board.id === boardId && snapshot.game.status === 'playing';
    if (eligible) snapshot.extraHints++;
    await save(); render(false); status(eligible ? 'adEarned' : 'adDismissed');
  } else status('adDismissed');
});
element('hint-button').addEventListener('click', () => {
  if (busy || nativeAdActive || !isActive() || !snapshot.game) return;
  unlockAudio();
  if (snapshot.game.status === 'completed') { void newBoard(); return; }
  if (snapshot.game.hints < 1 && snapshot.extraHints > 0) { snapshot.extraHints--; snapshot.game = { ...snapshot.game, hints: snapshot.game.hints + 1 }; }
  const result = useHint(snapshot.game);
  if (!result) { status('noHints'); return; }
  snapshot.game = result.state; hintCells = result.cells;
  const word = snapshot.game.board.targets.find(target => JSON.stringify(target.cells) === JSON.stringify(hintCells))?.word ?? '';
  clearTimeout(hintTimer); render(false); status('hintUsed', { word }); void save();
  const boardId = snapshot.game.board.id;
  hintTimer = window.setTimeout(() => { if (snapshot.game?.board.id !== boardId) return; hintCells = []; render(false); }, 5000);
});
element('focus-button').addEventListener('click', () => {
  clearSelection();
  if (document.body.classList.contains('focus-view')) void leaveFocus();
  else void enterFocus();
});
element('words-button').addEventListener('click', () => {
  const list = element('target-list').cloneNode(true) as HTMLElement;
  element('dialog-targets').replaceChildren(...Array.from(list.children));
  element<HTMLDialogElement>('words-dialog').showModal();
});
async function syncTilt(gesture = false): Promise<void> {
  if (!snapshot.preferences.tilt) tilt.disable();
  else if (!tilt.enabled && !tiltRequestPending) {
    const motion = window.DeviceMotionEvent as typeof DeviceMotionEvent & { requestPermission?: () => Promise<string> };
    if (motion?.requestPermission && (!gesture || tiltPermissionAttempted)) return;
    tiltRequestPending = true;
    if (gesture) tiltPermissionAttempted = true;
    try {
      const result = await tilt.enable();
      if (!snapshot.preferences.tilt) tilt.disable();
      else if (!isActive() || nativeAdActive || adResolve) tilt.suspend();
      if (gesture) status(result === 'enabled' ? 'tiltEnabled' : result === 'secure' ? 'tiltSecure' : result === 'denied' ? 'tiltDenied' : 'tiltUnavailable');
    } finally { tiltRequestPending = false; }
  }
  element('tilt-button').textContent = t(tilt.enabled ? 'tiltOff' : 'tilt');
}
element('tilt-button').addEventListener('click', () => {
  snapshot.preferences.tilt = !tilt.enabled; tiltPermissionAttempted = false;
  void syncTilt(true); applyPreferences(); void save();
});
document.addEventListener('pointerdown', event => {
  if (!(event.target as Element).closest('#tilt-button, [name="tilt"]') && snapshot.preferences.tilt && !tilt.enabled && !tiltPermissionAttempted) void syncTilt(true);
}, { capture: true });
document.addEventListener('keydown', event => {
  if (!(event.target as Element).closest('#tilt-button, [name="tilt"]') && snapshot.preferences.tilt && !tilt.enabled && !tiltPermissionAttempted) void syncTilt(true);
}, { capture: true });
element('new-button').addEventListener('click', () => { unlockAudio(); void newBoard(); });
element('continue-button').addEventListener('click', async () => {
  element<HTMLDialogElement>('results').close();
  await newBoard();
});

function populateNextPacks(selected: string): void {
  const locale = element<HTMLSelectElement>('next-word-locale').value;
  const choices = availablePacks.filter(pack => pack.language.locale === locale);
  const previous = availablePacks.find(pack => pack.id === selected);
  const select = element<HTMLSelectElement>('next-pack');
  select.replaceChildren(...choices.map(pack => {
    const option = document.createElement('option'); option.value = pack.id; option.textContent = packTitle(pack); return option;
  }));
  const option = document.createElement('option'); option.value = 'rotating'; option.textContent = t('pack.rotating'); select.append(option);
  select.value = selected === 'rotating' ? selected : (choices.find(pack => pack.id === selected || pack.nameKey === previous?.nameKey) ?? choices[0]!).id;
}
function showNextGameChoice(): void {
  const dialog = element<HTMLDialogElement>('next-game');
  if (dialog.open) return;
  pendingResults = null; scenery.cancel(); cancelFeedback(); clearSelection();
  element<HTMLSelectElement>('next-word-locale').value = snapshot.preferences.wordLocale;
  element<HTMLSelectElement>('next-challenge').value = snapshot.preferences.challenge;
  element<HTMLInputElement>('remember-next-choice').checked = false;
  populateNextPacks(snapshot.preferences.pack); dialog.showModal();
}
let startingNextChoice = false;
async function startNextChoice(saved: boolean): Promise<void> {
  if (startingNextChoice || busy || nativeAdActive || adResolve || !isActive()) return;
  const wordLocale = element<HTMLSelectElement>('next-word-locale').value, challenge = element<HTMLSelectElement>('next-challenge').value;
  const pack = element<HTMLSelectElement>('next-pack').value;
  if (!isChallenge(challenge) || !['en', 'es'].includes(wordLocale)) return;
  const choice: NextGameChoice = saved ? { pack: snapshot.preferences.pack, challenge: snapshot.preferences.challenge, wordLocale: snapshot.preferences.wordLocale } : { pack, challenge, wordLocale };
  startingNextChoice = true;
  try {
    if (!saved && element<HTMLInputElement>('remember-next-choice').checked) {
      Object.assign(snapshot.preferences, choice); applyPreferences(); await save();
    }
    if (!isActive()) return;
    element<HTMLDialogElement>('next-game').close();
    await newBoard(false, choice);
  } finally { startingNextChoice = false; }
}
element('next-word-locale').addEventListener('change', () => populateNextPacks(element<HTMLSelectElement>('next-pack').value));
element('next-game-form').addEventListener('submit', event => { event.preventDefault(); unlockAudio(); void startNextChoice(false); });
element('use-saved-choice').addEventListener('click', () => { unlockAudio(); void startNextChoice(true); });
element('preferences-button').addEventListener('click', () => element<HTMLDialogElement>('preferences').showModal());
// Restored ambient preferences need a trusted gesture under browser autoplay policies.
for (const type of ['pointerdown', 'keydown']) document.addEventListener(type, () => {
  if (!busy && isActive() && !nativeAdActive && !adResolve && snapshot.preferences.music) sound.resume(true);
}, { capture: true });

function unlockAudio(): void { if (snapshot.preferences.sound || snapshot.preferences.music) sound.unlock(); }
function renderAudioState(): void {
  element('audio-status').textContent = t(`audio.${sound.state}`);
  element('audio-status').dataset.state = sound.state;
  const retry = ['idle', 'blocked', 'unavailable'].includes(sound.state) && (snapshot.preferences.sound || snapshot.preferences.music);
  element('audio-retry').hidden = !retry;
  element('play-audio-retry').hidden = !retry || busy || !isActive() || nativeAdActive || Boolean(adResolve) || sound.state === 'idle';
}
for (const id of ['audio-retry', 'play-audio-retry']) element(id).addEventListener('click', () => {
  if (!isActive() || nativeAdActive || adResolve) return;
  unlockAudio(); sound.resume(snapshot.preferences.music); renderAudioState();
});

element('privacy-button').addEventListener('click', () => {
  if (nativeAdActive) return;
  element<HTMLDialogElement>('privacy').showModal();
});
element('ad-privacy-choices').addEventListener('click', async () => {
  if (!nativeAdsEnabled || nativeAdActive || !isActive()) return;
  const button = element<HTMLButtonElement>('ad-privacy-choices'); button.disabled = true;
  try { await ads.privacy(); }
  catch { status('privacyUnavailable'); }
  finally { button.disabled = false; }
});
document.querySelectorAll<HTMLButtonElement>('[data-close]').forEach(button => button.addEventListener('click', () => element<HTMLDialogElement>(button.dataset.close!).close()));
element('preferences-form').addEventListener('submit', event => event.preventDefault());
element('preferences-form').addEventListener('input', event => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !['effectsVolume', 'musicVolume'].includes(input.name)) return;
  const key = input.name as 'effectsVolume' | 'musicVolume';
  snapshot.preferences[key] = Math.max(0, Math.min(1, input.valueAsNumber));
  sound.volumes(snapshot.preferences.effectsVolume, snapshot.preferences.musicVolume);
});
element('preferences-form').addEventListener('change', event => {
  const form = element<HTMLFormElement>('preferences-form'), values = new FormData(form);
  for (const key of ['sound', 'music', 'reducedMotion', 'contrast', 'roomier', 'wake', 'tilt', 'askNextGame'] as const) snapshot.preferences[key] = values.get(key) === 'on';
  if ((event.target as HTMLInputElement).name === 'tilt' && snapshot.preferences.tilt) { tiltPermissionAttempted = false; void syncTilt(true); }
  snapshot.preferences.locale = String(values.get('locale')); snapshot.preferences.pack = String(values.get('pack')); snapshot.preferences.size = String(values.get('size'));
  snapshot.preferences.wordLocale = String(values.get('wordLocale'));
  const challenge = values.get('challenge');
  if (isChallenge(challenge)) snapshot.preferences.challenge = challenge;
  for (const key of ['effectsVolume', 'musicVolume'] as const) snapshot.preferences[key] = Math.max(0, Math.min(1, Number(values.get(key))));
  Object.assign(snapshot.preferences, customDimensions(Number(values.get('rows')), Number(values.get('columns')), isPhone()));
  applyPreferences(); void manageWakeLock(); void save();
});
element('export-button').addEventListener('click', async () => {
  const button = element<HTMLButtonElement>('export-button'); button.disabled = true;
  message('export-status', 'exportPreparing');
  try {
    if (!await save()) { message('export-status', 'exportSaveFailed'); return; }
    const data = await storage.exportSnapshot();
    const result = await saveProgressFile(JSON.stringify(data, null, 2), Capacitor.getPlatform() === 'android', nativePlatform, json => {
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a'); a.href = url; a.download = 'wordhuli-progress.json'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
    message('export-status', `export.${result}`);
  } catch (error) {
    message('export-status', error && typeof error === 'object' && 'code' in error && error.code === 'EXPORT_TOO_LARGE' ? 'exportTooLarge' : 'exportFailed');
  }
  finally { button.disabled = false; }
});

let licenseText: string | null = null, licenseLoading = false;
element<HTMLDetailsElement>('license-notices').addEventListener('toggle', async () => {
  if (!element<HTMLDetailsElement>('license-notices').open || licenseLoading) return;
  if (licenseText !== null) { element('license-text').textContent = licenseText; return; }
  licenseLoading = true; element('license-text').textContent = t('licensesLoading');
  try {
    const response = await fetch('/THIRD-PARTY-NOTICES.txt');
    if (!response.ok) throw new Error('Notices unavailable');
    licenseText = await response.text(); element('license-text').textContent = licenseText;
  } catch { element('license-text').textContent = t('licensesUnavailable'); }
  finally { licenseLoading = false; }
});
element('reset-button').addEventListener('click', async () => {
  if (!confirm(t('confirmReset'))) return;
  try { await storage.reset(); }
  catch { status('resetFailed'); message('content-status', 'resetFailed'); return; }
  snapshot = defaultSnapshot(); applyPreferences(); element<HTMLDialogElement>('preferences').close(); await newBoard(false);
});
let artAction = 0;
let artDownloads = 0, artDeletes = 0, artSettings = 0, artStorageEpoch = 0;
let artRetained = new Set<string>();
let artAutomatic = false;
function renderArtControls(): void {
  const busy = artDownloads + artDeletes + artSettings > 0;
  element<HTMLButtonElement>('download-button').disabled = busy;
  element<HTMLSelectElement>('art-pack-select').disabled = busy;
  element<HTMLButtonElement>('delete-downloads-button').disabled = artDeletes + artSettings > 0;
  element<HTMLSelectElement>('art-storage-budget').disabled = busy;
  element<HTMLButtonElement>('art-cleanup-button').disabled = busy;
  const keep = element<HTMLInputElement>('art-retain');
  keep.disabled = busy || !artUrls.has(selectedArtPack().id);
  if (!busy) keep.checked = artRetained.has(selectedArtPack().id);
  element<HTMLInputElement>('art-automatic').disabled = busy;
  if (!busy) element<HTMLInputElement>('art-automatic').checked = artAutomatic;
  if (artDeletes) element<HTMLButtonElement>('cancel-download-button').disabled = true;
}
const selectedArtPack = () => optionalArtPack(element<HTMLSelectElement>('art-pack-select').value);
async function renderArtStorage(): Promise<void> {
  const epoch = ++artStorageEpoch;
  try {
    const state = await artStorageState(); if (epoch !== artStorageEpoch) return;
    const format = (bytes: number) => new Intl.NumberFormat(snapshot.preferences.locale, { maximumFractionDigits: 2 }).format(bytes / 1048576);
    element('art-storage-usage').textContent = t('artStorageUsage', { used: format(state.used), limit: format(state.budget) });
    element<HTMLSelectElement>('art-storage-budget').value = String(state.budget);
    artRetained = new Set(state.retained); artAutomatic = state.automatic; renderArtControls();
  } catch { if (epoch === artStorageEpoch) message('art-storage-usage', 'artStorageUnavailable'); }
}
element('preferences-button').addEventListener('click', () => { void renderArtStorage(); });
element('art-automatic').addEventListener('change', async () => {
  const automatic = element<HTMLInputElement>('art-automatic').checked;
  artStorageEpoch++; artSettings++; renderArtControls();
  try { await setArtAutomatic(automatic); message('content-status', 'artAutomaticSaved'); }
  catch (error) { message('content-status', error instanceof ArtCleanupUnavailable ? 'artCleanupUnavailable' : 'artStorageUnavailable'); }
  finally { artSettings--; await renderArtStorage(); renderArtControls(); }
});
element('art-retain').addEventListener('change', async () => {
  const pack = selectedArtPack(), retained = element<HTMLInputElement>('art-retain').checked;
  artStorageEpoch++; artSettings++; renderArtControls();
  try { await setArtRetention(pack.id, retained); message('content-status', 'artRetentionSaved'); }
  catch { message('content-status', 'artStorageUnavailable'); }
  finally { artSettings--; await renderArtStorage(); renderArtControls(); }
});
element('art-cleanup-button').addEventListener('click', async () => {
  artStorageEpoch++; artSettings++; renderArtControls();
  try {
    const removed = await clearUnretainedArt(() => snapshot.game ? [currentPack().nameKey.split('.').at(-1)!] : []);
    for (const id of removed) forgetArt(id);
    renderArtPackDetails(); if (snapshot.game) render(false);
    element('content-status').textContent = t('artCleanupDone', { count: String(removed.length) });
  } catch (error) { message('content-status', error instanceof ArtCleanupUnavailable ? 'artCleanupUnavailable' : 'artStorageUnavailable'); }
  finally { await showArt(); artSettings--; await renderArtStorage(); renderArtControls(); }
});
element('art-storage-budget').addEventListener('change', async () => {
  const budget = Number(element<HTMLSelectElement>('art-storage-budget').value);
  artStorageEpoch++; artSettings++; renderArtControls();
  try { await setArtBudget(budget); message('content-status', 'artBudgetSaved'); }
  catch (error) { message('content-status', error instanceof ArtBudgetError ? 'artBudgetTooSmall' : 'artStorageUnavailable'); }
  finally { artSettings--; renderArtControls(); await renderArtStorage(); }
});
function renderArtPackDetails(): void {
  const pack = selectedArtPack();
  const size = new Intl.NumberFormat(snapshot.preferences.locale, { style: 'unit', unit: 'kilobyte', maximumFractionDigits: 0 }).format((artUrls.get(pack.id)?.bytes ?? pack.bytes) / 1000);
  element('art-pack-details').textContent = t(artUrls.has(pack.id) ? 'artPackReady' : 'artPackAvailable', { size });
  element('mock-banner').querySelector<HTMLImageElement>('img')!.src = artUrls.get(pack.id)?.portrait.src ?? '/art/mock-explorer.svg';
  renderArtControls();
}
element('art-pack-select').addEventListener('change', renderArtPackDetails);
element('download-button').addEventListener('click', async () => {
  const action = ++artAction, pack = selectedArtPack(); artDownloads++; renderArtControls();
  const progress = element<HTMLProgressElement>('art-download-progress'), cancel = element<HTMLButtonElement>('cancel-download-button');
  element('art-download-state').hidden = false; progress.removeAttribute('value'); cancel.disabled = false;
  message('art-download-label', 'downloadStarting');
  try {
    const evicted = await downloadArtPack(state => {
      if (action !== artAction) return;
      progress.max = state.total; progress.value = state.received; cancel.disabled = state.saving;
      const format = (bytes: number) => new Intl.NumberFormat(snapshot.preferences.locale, { style: 'unit', unit: 'kilobyte', maximumFractionDigits: 0 }).format(bytes / 1000);
      element('art-download-label').textContent = state.saving ? t('downloadSaving') : t('downloadProgress', { received: format(state.received), total: format(state.total) });
    }, pack.id);
    if (action !== artAction) return;
    await showArt();
    if (action === artAction) {
      message('content-status', 'downloaded');
      if (evicted.length) element('content-status').textContent = t('downloadedAfterCleanup', { count: String(evicted.length) });
    }
  } catch (error) {
    if (action === artAction && !(error instanceof DOMException && error.name === 'AbortError')) message('content-status', error instanceof ArtBudgetError ? 'artBudgetExceeded' : error instanceof ArtCleanupUnavailable ? 'artCleanupUnavailable' : 'downloadError');
  } finally { artDownloads--; renderArtControls(); element('art-download-state').hidden = true; void renderArtStorage(); }
});

element('cancel-download-button').addEventListener('click', () => {
  artAction++; cancelArtDownload();
  element<HTMLButtonElement>('cancel-download-button').disabled = true;
  message('content-status', 'downloadCancelled');
});
element('delete-downloads-button').addEventListener('click', async () => {
  const action = ++artAction, pack = selectedArtPack(); artDeletes++; renderArtControls();
  try {
    await deleteArtPack(pack.id);
    if (action !== artAction) return;
    forgetArt(pack.id);
    renderArtPackDetails();
    void renderArtStorage();
    if (snapshot.game) render(false);
    message('content-status', 'downloadsDeleted');
  } catch { if (action === artAction) message('content-status', 'deleteError'); }
  finally { artDeletes--; renderArtControls(); }
});
element('generate-button').addEventListener('click', async () => {
  const button = element<HTMLButtonElement>('generate-button'); button.disabled = true;
  try {
    const pack = await generateTheme(element<HTMLInputElement>('theme-topic').value, lexiconFor(snapshot.preferences.wordLocale));
    availablePacks.push(pack); snapshot.generatedPacks = [...(snapshot.generatedPacks ?? []), pack].slice(-10);
    snapshot.preferences.pack = pack.id; applyPreferences(); await save(); message('content-status', 'generated');
  } catch { message('content-status', 'generateError'); }
  finally { button.disabled = false; }
});
async function showArt(): Promise<void> {
  for (const pack of optionalArtPacks) {
    try {
      const url = await cachedArt(pack.id);
      if (url) { forgetArt(pack.id); artUrls.set(pack.id, url); }
      else forgetArt(pack.id);
    } catch { /* Optional storage cannot block gameplay. */ }
  }
  renderArtPackDetails(); if (snapshot.game) render(false);
  void renderArtStorage();
}
function renderAwakeState(): void {
  element('wake-status').textContent = t(`wakeState.${awake.state}`);
  element('wake-status').dataset.state = awake.state;
}
function manageWakeLock(): Promise<void> {
  return awake.update(snapshot.preferences.wake, isActive() && snapshot.game?.status === 'playing' && !nativeAdActive && !adResolve);
}
function updateLifecycle(remeasure = false): void {
  const active = isActive();
  if (active === lifecycleActive && !remeasure) return;
  lifecycleActive = active;
  if (!isActive()) { foregroundGeneration?.abort(); scenery.cancel(); preparedBoards.clear(); cancelFeedback(); if (snapshot.game) render(false); }
  clearSelection(); void save(); void manageWakeLock();
  if (!isActive()) { tilt.suspend(); sound.suspend(); }
  else if (!adResolve && !nativeAdActive) { tilt.resume(); sound.resume(snapshot.preferences.music); }
  if (isActive()) {
    if (saveReady && !snapshot.game && !busy) void newBoard(false);
    revealResults(); warmNextBoards(); requestAnimationFrame(fitBoard); void refreshOfflineState();
  }
  renderAudioState(); renderBannerControls(); syncBannerVisibility();
}
document.addEventListener('visibilitychange', () => updateLifecycle());
document.addEventListener('freeze', () => { pageFrozen = true; updateLifecycle(); });
document.addEventListener('resume', () => { pageFrozen = false; updateLifecycle(); });
window.addEventListener('wordhuli:pause', () => { nativeActive = false; updateLifecycle(); });
window.addEventListener('wordhuli:resume', () => { nativeActive = true; updateLifecycle(); });
installNativeBack({
  blocked: () => nativeAdActive || !isActive(),
  cancelSelection: () => { if (selectionStart === null && !pointer) return false; clearSelection(); return true; },
  leaveFocus: () => {
    if (!document.body.classList.contains('focus-view')) return false;
    void leaveFocus(); return true;
  },
  returnToBoard: () => {
    if (location.hash !== '#target-words') return false;
    history.replaceState(null, '', '#play-board'); element('play-board').scrollIntoView({ block: 'start' }); return true;
  },
  save,
  background: async () => { if (Capacitor.isNativePlatform()) await nativePlatform.background(); },
});
window.addEventListener('pagehide', () => { pagePresent = false; updateLifecycle(); });
window.addEventListener('pageshow', event => {
  const returning = !pagePresent || pageFrozen || event.persisted;
  pagePresent = true; pageFrozen = false;
  if (returning) updateLifecycle(true);
});
for (const event of ['online', 'offline']) window.addEventListener(event, () => { element('connection').textContent = t(navigator.onLine ? 'online' : 'offline'); });

function renderInstallState(): void {
  const native = Capacitor.isNativePlatform();
  webInstall.installed = native || webInstall.state === 'installed' || installedDisplay.matches || (fullscreenDisplay.matches && !document.fullscreenElement);
  webInstall.engaged = snapshot.records.length > 0 || (snapshot.game?.credited.length ?? 0) >= 3;
  webInstall.offline = offlineState === 'ready';
  element('web-install-settings').hidden = native;
  element('install-offer').hidden = native || !webInstall.offer || busy || !isActive() || nativeAdActive || feedback.running || Boolean(document.querySelector('dialog[open]'));
  for (const id of ['web-install-button', 'offer-install-button']) element<HTMLButtonElement>(id).disabled = !webInstall.available;
  element('web-install-button').hidden = !webInstall.available;
  const state = webInstall.installed ? 'installed' : webInstall.state === 'accepted' ? 'accepted'
    : webInstall.state === 'prompting' ? 'prompting' : webInstall.state === 'failed' ? 'failed'
    : !webInstall.engaged ? 'playFirst' : !webInstall.offline ? 'prepareFirst' : webInstall.available ? 'available' : 'manual';
  element('web-install-status').textContent = t(`install.${state}`);
  element('web-install-status').dataset.state = state;
}
window.addEventListener('beforeinstallprompt', event => {
  if (!Capacitor.isNativePlatform()) webInstall.capture(event as InstallPrompt);
});
window.addEventListener('appinstalled', () => { webInstall.markInstalled(); renderInstallState(); });
for (const id of ['web-install-button', 'offer-install-button']) element(id).addEventListener('click', () => {
  if (isActive() && !nativeAdActive && !adResolve) void webInstall.prompt();
});
element('dismiss-install-button').addEventListener('click', () => webInstall.dismiss());
new MutationObserver(renderInstallState).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });

function renderOfflineState(): void {
  element('offline-status').textContent = t(`offlineState.${offlineState}`);
  element('offline-status').dataset.state = offlineState;
  element('offline-check').hidden = offlineState === 'bundled';
  renderInstallState();
}
async function refreshOfflineState(): Promise<void> {
  const epoch = ++offlineEpoch;
  offlineState = 'checking'; renderOfflineState();
  const result = await offlineReadiness(import.meta.env.VITE_BUILD_ID, Capacitor.isNativePlatform());
  if (epoch !== offlineEpoch) return;
  offlineState = result; renderOfflineState();
}
element('offline-check').addEventListener('click', () => { void refreshOfflineState(); });
for (const event of ['online', 'offline']) window.addEventListener(event, () => { void refreshOfflineState(); });

async function setupWorker(): Promise<void> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || Capacitor.isNativePlatform()) return;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
    const waiting = () => { if (registration.waiting && navigator.serviceWorker.controller) { waitingWorker = registration.waiting; element('update-banner').hidden = false; } };
    waiting(); registration.addEventListener('updatefound', () => registration.installing?.addEventListener('statechange', waiting));
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => { void refreshOfflineState(); if (waitingWorker && !refreshing) { refreshing = true; location.reload(); } });
    const check = () => { if (!document.hidden && navigator.onLine) void registration.update().catch(() => {}); };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    check();
  } catch { /* Installation failure leaves current online gameplay usable. */ }
}
element('update-button').addEventListener('click', async () => { if (await save()) waitingWorker?.postMessage({ type: 'SKIP_WAITING' }); });

async function boot(): Promise<void> {
  setLocale('en'); translatePage(); status('loadingDictionary');
  element('build-id').textContent = `Build ${import.meta.env.VITE_BUILD_ID}`;
  element('refresh-link').hidden = Capacitor.isNativePlatform();
  element('review-tools').hidden = Capacitor.isNativePlatform();
  const englishCommonForms = (async (): Promise<string[]> => {
    try {
      const response = await fetch('/content/english-common-forms.json'); if (!response.ok) throw new Error('English additions unavailable');
      const data = await response.json() as { version: string; locale: string; words: string[] };
      if (data.version !== 'wordhuli-en-common-spell-v1' || data.locale !== 'en' || !Array.isArray(data.words) || data.words.length > 50000
        || !data.words.every(word => typeof word === 'string' && /^[A-Z]{3,32}$/.test(word))) throw new Error('Invalid English additions');
      return data.words;
    } catch { return []; }
  })();
  const frequencyData = Promise.allSettled(['en', 'es'].map(async locale => {
    const response = await fetch(`/content/frequency-${locale}.json`); if (!response.ok) throw new Error('Frequency data unavailable');
    const data = await response.json() as { version: string; locale: string; entries: Array<[string, number]> };
    const pattern = locale === 'en' ? /^[A-Z]{3,32}$/ : /^[A-ZÁÉÍÓÚÜÑ]{3,32}$/u;
    if (data.version !== 'wordfreq-3.1.1-cb-v1' || data.locale !== locale || !Array.isArray(data.entries) || data.entries.length > 50000
      || !data.entries.every(entry => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string' && pattern.test(entry[0]) && Number.isInteger(entry[1]) && entry[1] >= 0 && entry[1] <= 700)) throw new Error('Invalid frequency data');
    frequencyRanks.set(locale, new Map(data.entries));
  }));
  const spanishDictionary = (async () => {
    try {
      const response = await fetch('/content/spanish.json'); if (!response.ok) throw new Error('Spanish dictionary unavailable');
      const data = await response.json() as { version: string; locale: string; words: string[]; blocked: string[] };
      const validWord = (word: unknown): word is string => typeof word === 'string' && /^[A-ZÁÉÍÓÚÜÑ]{3,32}$/u.test(word);
      if (data.version !== dictionaryVersion('es') || data.locale !== 'es' || !Array.isArray(data.words) || data.words.length > 200000
        || !data.words.every(validWord) || !Array.isArray(data.blocked) || !data.blocked.every(validWord)) throw new Error('Invalid Spanish dictionary');
      const permitted = new Set([...data.words, ...packsForLocale('es').flatMap(pack => pack.words)]), blocked = new Set(data.blocked);
      for (const word of blocked) permitted.delete(word);
      lexicons.es = { language: spanish, permitted, blocked };
    } catch { /* Curated Spanish targets/discoveries stay playable without the broad dictionary. */ }
  })();
  try {
    const response = await fetch('/content/english.json'); if (!response.ok) throw new Error('Dictionary unavailable');
    const data = await response.json() as { version: string; words: string[]; blocked: string[] };
    lexicons.en = { language: english, permitted: new Set([...data.words, ...packsForLocale('en').flatMap(pack => pack.words)]), blocked: new Set(data.blocked) };
    availablePacks = availablePacks.map(pack => pack.id === 'general' ? { ...pack, words: data.words.filter(word => /^[A-Z]{3,16}$/.test(word) && !lexicons.en!.blocked.has(word)) } : pack);
  } catch { /* Bundled curated target lists provide a playable degraded fallback. */ }
  for (const word of await englishCommonForms) if (!lexicons.en!.blocked.has(word)) lexicons.en!.permitted.add(word);
  await spanishDictionary;
  await frequencyData;
  try {
    snapshot = await storage.load();
    // Seed older saves from completed records and the current (possibly abandoned) board.
    if (!Object.keys(snapshot.exposure ?? {}).length) {
      for (const record of snapshot.records) if (Array.isArray(record.targets)) {
        const packId = record.boardId.split(':')[0]!;
        const locale = snapshot.generatedPacks?.find(pack => pack.id === packId)?.language.locale ?? (packId.endsWith('-es') ? 'es' : 'en');
        snapshot.exposure = rememberTargets(snapshot.exposure ?? {}, locale, record.targets);
      }
    }
    if (snapshot.game) snapshot.exposure = rememberTargets(snapshot.exposure ?? {}, snapshot.game.board.locale, snapshot.game.board.targets.map(target => target.word));
    if (snapshot.game) snapshot.recentThemes = rememberTheme(snapshot.recentThemes ?? {}, snapshot.game.board.locale, snapshot.game.board.packId);
    if (snapshot.game?.status === 'playing' && snapshot.game.rulesVersion === 'classic-2') snapshot.game.rulesVersion = 'classic-3';
    if (snapshot.game?.status !== 'playing') snapshot.extraHints = 0;
    for (const pack of snapshot.generatedPacks ?? []) {
      try {
        if (!['en', 'es'].includes(pack.language.locale)) continue;
        const lexicon = lexiconFor(pack.language.locale), valid = validateGenerated(pack, lexicon);
        availablePacks.push({ ...pack, ...valid, language: lexicon.language });
      } catch { /* Ignore incompatible optional content. */ }
    }
    if (snapshot.game && (!availablePacks.some(pack => pack.id === snapshot.game!.board.packId && compatiblePackVersion(pack, snapshot.game!.board.packVersion) && pack.language.locale === snapshot.game!.board.locale) || snapshot.game.board.dictionaryVersion !== dictionaryVersion(snapshot.game.board.locale))) {
      try { await storage.preserveLoaded(); storage.notice = 'content'; } catch { /* Protect the unreadable save if backup storage fails. */ }
      snapshot.game = null; snapshot.extraHints = 0;
    }
    if (!snapshot.game && innerWidth >= 1100) snapshot.preferences.size = 'expanded';
    else if (!snapshot.game && innerWidth >= 760) snapshot.preferences.size = 'roomy';
  } catch { status('recoverFailed'); }
  saveReady = true;
  if (nativeAdsEnabled) void nativeAdBridge.addListener('bannerState', updateBannerState).catch(() => {});
  if (ads instanceof NativeAds) {
    try { await ads.recover(snapshot, save); } catch { /* Pending journal remains for another launch. */ }
  }
  applyPreferences(); busy = false;
  void lockPhonePortrait();
  if (snapshot.game) { render(true); status('ready'); element<HTMLButtonElement>('new-button').disabled = false; await save(); }
  else await newBoard(false);
  warmNextBoards();
  await setupWorker(); void refreshOfflineState(); renderAudioState(); await showArt();
}
void boot().catch(() => { busy = false; status('generationError'); });
